import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminTerms.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSave, FaPlus, FaTrash, FaRegFileAlt, FaHeading, FaParagraph, FaListUl } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const AdminTerms = () => {
    const [isSaving, setIsSaving] = useState(false);
    const [termsId, setTermsId] = useState(null);
    const [termsData, setTermsData] = useState({
        hero: {
            titlePart: 'Terms',
            highlight: '&',
            suffix: 'Conditions',
            description: 'Please read these terms carefully before using Nimai Solutions\' website and services.'
        },
        sections: [
            {
                id: Date.now(),
                icon: 'gavel',
                title: 'Acceptance of Terms',
                content: 'By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement...',
                points: []
            }
        ],
        lastUpdated: 'February 6, 2026'
    });

    useEffect(() => {
        fetchTerms();
    }, []);

    const fetchTerms = async () => {
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/term-conditions', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });
            let terms = null;

            if (Array.isArray(result)) {
                terms = result[0];
            } else if (result.data) {
                terms = Array.isArray(result.data) ? result.data[0] : result.data;
            }

            if (ok && terms) {
                setTermsId(terms.id);
                setTermsData({
                    hero: {
                        titlePart: terms.hero_title_part1,
                        highlight: terms.hero_highlight_symbol,
                        suffix: terms.hero_title_suffix,
                        description: terms.hero_description
                    },
                    sections: terms.sections.map((s, idx) => ({
                        id: s.id || idx,
                        icon: s.icon_type,
                        title: s.section_title,
                        content: s.content,
                        points: s.list_items || []
                    })),
                    lastUpdated: terms.last_updated_date
                });
            }
        } catch (error) {
            console.error('Error fetching terms:', error);
            toast.error('Failed to load terms & conditions');
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        const method = termsId ? 'PUT' : 'POST';
        const url = termsId
            ? `https://aayushrealproperties.in/nimaibackend/public/api/term-conditions/${termsId}`
            : 'https://aayushrealproperties.in/nimaibackend/public/api/term-conditions';

        const payload = {
            hero_title_part1: termsData.hero.titlePart,
            hero_highlight_symbol: termsData.hero.highlight,
            hero_title_suffix: termsData.hero.suffix,
            hero_description: termsData.hero.description,
            last_updated_date: termsData.lastUpdated,
            sections: termsData.sections.map(s => ({
                icon_type: s.icon,
                section_title: s.title,
                content: s.content,
                list_items: s.points
            }))
        };

        try {
            const { ok, data: result } = await safeFetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (ok) {
                if (!termsId) setTermsId(result.id);
                toast.success('Terms & Conditions updated successfully!');
            } else {
                toast.error('Failed to update terms & conditions');
            }
        } catch (error) {
            console.error('Error saving terms:', error);
            toast.error('Network error while saving');
        } finally {
            setIsSaving(false);
        }
    };

    const addSection = () => {
        const newSection = {
            id: Date.now(),
            icon: 'gavel',
            title: 'New Section Title',
            content: 'Section content goes here...',
            points: []
        };
        setTermsData({ ...termsData, sections: [...termsData.sections, newSection] });
    };

    const removeSection = (id) => {
        setTermsData({ ...termsData, sections: termsData.sections.filter(s => s.id !== id) });
    };

    const updateSection = (id, field, value) => {
        const newSections = termsData.sections.map(s => {
            if (s.id === id) return { ...s, [field]: value };
            return s;
        });
        setTermsData({ ...termsData, sections: newSections });
    };

    const addPoint = (sectionId) => {
        const newSections = termsData.sections.map(s => {
            if (s.id === sectionId) return { ...s, points: [...s.points, 'New point'] };
            return s;
        });
        setTermsData({ ...termsData, sections: newSections });
    };

    const removePoint = (sectionId, pointIdx) => {
        const newSections = termsData.sections.map(s => {
            if (s.id === sectionId) return { ...s, points: s.points.filter((_, i) => i !== pointIdx) };
            return s;
        });
        setTermsData({ ...termsData, sections: newSections });
    };

    const updatePoint = (sectionId, pointIdx, value) => {
        const newSections = termsData.sections.map(s => {
            if (s.id === sectionId) {
                const newPoints = [...s.points];
                newPoints[pointIdx] = value;
                return { ...s, points: newPoints };
            }
            return s;
        });
        setTermsData({ ...termsData, sections: newSections });
    };

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Edit <span>Terms & Conditions</span></h1>
                        <p>Manage your professional service agreements and user obligations.</p>
                    </div>
                    <div className="header-actions">
                        <button className="save-btn" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : <><FaSave /> Save Changes</>}
                        </button>
                    </div>
                </header>

                <div className="admin-terms-container">
                    {/* Hero Section Edit */}
                    <div className="admin-card-section">
                        <div className="section-header">
                            <h3><FaHeading /> Hero Section</h3>
                        </div>
                        <div className="admin-form-grid">
                            <div className="admin-input-group">
                                <label>Title Part 1</label>
                                <input type="text" value={termsData.hero.titlePart} onChange={(e) => setTermsData({ ...termsData, hero: { ...termsData.hero, titlePart: e.target.value } })} />
                            </div>
                            <div className="admin-input-group">
                                <label>Highlight Symbol/Word</label>
                                <input type="text" className="highlight-field" value={termsData.hero.highlight} onChange={(e) => setTermsData({ ...termsData, hero: { ...termsData.hero, highlight: e.target.value } })} />
                            </div>
                            <div className="admin-input-group full-width">
                                <label>Title Suffix</label>
                                <input type="text" value={termsData.hero.suffix} onChange={(e) => setTermsData({ ...termsData, hero: { ...termsData.hero, suffix: e.target.value } })} />
                            </div>
                            <div className="admin-input-group full-width">
                                <label>Description</label>
                                <textarea rows="3" value={termsData.hero.description} onChange={(e) => setTermsData({ ...termsData, hero: { ...termsData.hero, description: e.target.value } })}></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Policy Sections Edit */}
                    <div className="sections-list">
                        <div className="sections-header">
                            <h3><FaRegFileAlt /> Legal Sections</h3>
                            <button className="add-section-btn" onClick={addSection}><FaPlus /> Add New Section</button>
                        </div>

                        <AnimatePresence>
                            {termsData.sections.map((section, index) => (
                                <motion.div
                                    key={section.id}
                                    className="admin-terms-section-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                >
                                    <div className="card-top">
                                        <h4>Section #{index + 1}</h4>
                                        <button className="delete-btn" onClick={() => removeSection(section.id)}><FaTrash /></button>
                                    </div>

                                    <div className="admin-form-grid">
                                        <div className="admin-input-group">
                                            <label>Icon Type</label>
                                            <select value={section.icon} onChange={(e) => updateSection(section.id, 'icon', e.target.value)}>
                                                <option value="gavel">Gavel (Terms)</option>
                                                <option value="file">File (Description)</option>
                                                <option value="shield">Shield (IP)</option>
                                                <option value="alert">Alert (Liability)</option>
                                                <option value="handshake">Handshake (Termination)</option>
                                            </select>
                                        </div>
                                        <div className="admin-input-group">
                                            <label>Section Title</label>
                                            <input type="text" value={section.title} onChange={(e) => updateSection(section.id, 'title', e.target.value)} />
                                        </div>
                                        <div className="admin-input-group full-width">
                                            <label><FaParagraph /> Content</label>
                                            <textarea rows="4" value={section.content} onChange={(e) => updateSection(section.id, 'content', e.target.value)}></textarea>
                                        </div>

                                        <div className="admin-input-group full-width">
                                            <label><FaListUl /> List Items (Optional)</label>
                                            <div className="points-manager">
                                                {section.points.map((point, pIdx) => (
                                                    <div key={pIdx} className="point-row">
                                                        <input type="text" value={point} onChange={(e) => updatePoint(section.id, pIdx, e.target.value)} />
                                                        <button className="remove-point" onClick={() => removePoint(section.id, pIdx)}><FaTrash /></button>
                                                    </div>
                                                ))}
                                                <button className="add-point-btn" onClick={() => addPoint(section.id)}><FaPlus /> Add Point</button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    <div className="admin-card-section footer-meta">
                        <div className="admin-input-group">
                            <label>Last Updated Date</label>
                            <input type="text" value={termsData.lastUpdated} onChange={(e) => setTermsData({ ...termsData, lastUpdated: e.target.value })} />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminTerms;
